'use client'

import { Text } from '@quark/typography'
import { Container, Flex, Grid, GridItem } from '@quark/layout'
import React from 'react'
import { NewHomePageComponent } from '@components/HomePage/utils/HomeComponentType'
import { RatingProps } from '@interfaces/home'
import { Button } from '@quark/button'
import { Media } from '@quark/media'
import Image from 'next/image'
import { Layer } from '@quark/layer'
import { BrandVars } from '@quark/theme'
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import { Reveal } from '@quark/animation'
import styles from './rating.module.scss'

const Rating = (data: RatingProps) => {
  const handleClick = () => {
    void (async () => {
      await trackEvent('btn_get_app_link_initiated', {
        experiment_name: 'wrapper_v1',
        page_type: 'acko_home_page',
        redirection_url: data?.rightCardData?.commonProps?.appDownloadData?.redirectionUrl,
        journey: 'app_download_rating_section',
        header_name: 'app_download_rating_section',
      })
    })()
    window.open(data?.rightCardData?.commonProps?.appDownloadData?.redirectionUrl)
  }

  return (
    <Container
      id={NewHomePageComponent.RatingGrid}
      type="root"
      backgroundColor="base/primary"
      px={{
        lg: 'spacing-80',
        md: 'spacing-32',
        sm: 'spacing-16',
      }}
      py={{
        lg: 'spacing-80',
        md: 'spacing-56',
        sm: 'spacing-40',
      }}>
      <Reveal as="div">
        <Grid size="fullWidth" gap={{ lg: 'spacing-24', md: 'spacing-24', sm: 'spacing-16' }} gridTemplateColumns="12">
          <GridItem gridColumnStart="1" gridColumnEnd="6">
            <div className={styles.leftCard}>
              <Layer imgLg={data?.leftCardData?.bgImageProps?.src}>
                <Container padding="spacing-32" type="max-parent">
                  <Flex
                    size="fullSize"
                    flexDirection="column"
                    gap={{ lg: 'spacing-12', md: 'spacing-12', sm: 'spacing-8' }}
                    alignItems="center"
                    justifyContent="center">
                    <Text
                      as="span"
                      fontStyle={{ lg: 'um_text-5xl/bold', md: 'um_text-4xl/bold', sm: 'um_text-3xl/bold' }}
                      color="static/Primary-onDark"
                      textAlign="center"
                      whiteSpace="pre">
                      {data?.leftCardData?.heading}
                    </Text>
                    <Text
                      as="span"
                      fontStyle={{ lg: 'um_text-2xl/normal', md: 'um_text-lg/normal', sm: 'um_text-base/normal' }}
                      color="static/Primary-onDark"
                      textAlign="center"
                      whiteSpace="pre">
                      {data?.leftCardData?.subHeading}
                    </Text>
                  </Flex>
                </Container>
              </Layer>
            </div>
          </GridItem>
          <GridItem gridColumnStart={{ lg: '6', md: '1', sm: '1' }} gridColumnEnd="13">
            <div
              className={styles.rightCard}
              style={{
                ['--rightBgGradient' as any]:
                  'radial-gradient(598.75% 137.91% at 100% 97.12%, #BAA5FD 0%, #6815E4 100%)',
                ['--storeBgColor' as any]: BrandVars.Surface['gray/200'],
              }}>
              <Flex
                size="fullWidth"
                flexDirection="column"
                gap={{ lg: 'spacing-12', md: 'spacing-12', sm: 'spacing-8' }}>
                <Text
                  as="span"
                  display={{ lg: 'block', md: 'none', sm: 'none' }}
                  fontStyle={{ lg: 'um_text-4xl/bold', md: 'um_text-4xl/bold', sm: 'um_text-2xl/bold' }}
                  color="static/Primary-onDark"
                  textAlign={{ lg: 'left', md: 'center', sm: 'center' }}
                  whiteSpace="pre">
                  {data?.rightCardData?.desktopProps?.heading}
                </Text>
                <Text
                  as="span"
                  display={{ lg: 'none', md: 'block', sm: 'none' }}
                  fontStyle={{ lg: 'um_text-4xl/bold', md: 'um_text-4xl/bold', sm: 'um_text-2xl/bold' }}
                  color="static/Primary-onDark"
                  textAlign={{ lg: 'left', md: 'center', sm: 'center' }}
                  whiteSpace="pre">
                  {data?.rightCardData?.tabletProps?.heading}
                </Text>
                <Text
                  as="span"
                  display={{ lg: 'none', md: 'none', sm: 'block' }}
                  fontStyle={{ lg: 'um_text-4xl/bold', md: 'um_text-4xl/bold', sm: 'um_text-2xl/bold' }}
                  color="static/Primary-onDark"
                  textAlign={{ lg: 'left', md: 'center', sm: 'center' }}
                  whiteSpace="pre">
                  {data?.rightCardData?.mobileProps?.heading}
                </Text>
                <Text
                  as="span"
                  display={{ lg: 'block', md: 'none', sm: 'none' }}
                  fontStyle={{ lg: 'um_text-lg/normal', md: 'um_text-lg/normal', sm: 'um_text-sm/normal' }}
                  color="static/Secondary-onDark"
                  textAlign={{ lg: 'left', md: 'center', sm: 'center' }}>
                  {data?.rightCardData?.desktopProps?.subHeading}
                </Text>
                <Text
                  as="span"
                  display={{ lg: 'none', md: 'block', sm: 'none' }}
                  fontStyle={{ lg: 'um_text-lg/normal', md: 'um_text-lg/normal', sm: 'um_text-sm/normal' }}
                  color="static/Secondary-onDark"
                  textAlign={{ lg: 'left', md: 'center', sm: 'center' }}>
                  {data?.rightCardData?.tabletProps?.subHeading}
                </Text>
                <Text
                  as="span"
                  display={{ lg: 'none', md: 'none', sm: 'block' }}
                  fontStyle={{ lg: 'um_text-lg/normal', md: 'um_text-lg/normal', sm: 'um_text-sm/normal' }}
                  color="static/Secondary-onDark"
                  textAlign={{ lg: 'left', md: 'center', sm: 'center' }}>
                  {data?.rightCardData?.mobileProps?.subHeading}
                </Text>
              </Flex>
              <Grid size="fullWidth" columnGap={{ lg: 'spacing-24', md: 'spacing-24', sm: 'spacing-12' }} gridTemplateColumns="6">
                {data?.rightCardData?.commonProps?.stores?.map((store, idx) => (
                  <GridItem
                    gridColumn={{ lg: 'span-2', md: 'span-2', sm: 'span-3' }}
                    gridRow="span-1"
                    key={`store-${idx}`}>
                    <div className={styles.storeContainer} key={`store-${idx}`}>
                      <Image
                        className={styles.storeIcon}
                        src={store?.storeIconSrc ?? ''}
                        alt={store?.storeName ?? ''}
                        width={56}
                        height={56}
                      />
                      <Flex flexDirection="column" gap={{ lg: 'spacing-4', md: 'spacing-4', sm: 'spacing-0' }}>
                        <Flex gap="spacing-4" alignItems="center">
                          <Text
                            as="span"
                            fontStyle={{
                              lg: 'um_text-4xl/bold',
                              md: 'um_text-4xl/bold',
                              sm: 'um_text-xl/bold',
                            }}
                            color="static/Primary-onDark">
                            {store?.storeRating}
                          </Text>
                          <Image
                            className={styles.fullStarImage}
                            src={data?.rightCardData?.commonProps?.fullStarImageSrc ?? ''}
                            alt="Full Star"
                            width={32}
                            height={32}
                          />
                        </Flex>
                        <Text
                          as="span"
                          fontStyle={{
                            lg: 'um_text-lg/normal',
                            md: 'um_text-lg/normal',
                            sm: 'um_text-sm/normal',
                          }}
                          color="static/Secondary-onDark"
                          whiteSpace="nowrap">
                          {store?.storeName}&nbsp;
                          <span className={styles.storeNameAddition}>rating</span>
                        </Text>
                      </Flex>
                    </div>
                  </GridItem>
                ))}
                <GridItem key="qr-code" gridColumn="span-2" gridRow="span-1">
                  <Image
                    className={styles.qrImage}
                    src={data?.rightCardData?.commonProps?.appDownloadData?.qrImageSrc ?? ''}
                    alt="QR Code"
                    width={200}
                    height={200}
                  />
                </GridItem>
              </Grid>
              <div className={styles.downloadImage}>
                <Media
                  src={
                    data?.rightCardData?.mobileProps?.mobileDataImageSrc ??
                    'https://acko-cms-dev.s3.ap-south-1.amazonaws.com/mobile_Download_18a9ec8c4e.webp'
                  }
                  type="image"
                  size="fullWidth"
                  objectFit="contain"
                  objectPosition="bottom"
                />
                <Button type="staticOnDarkSolid" size="block" onClick={handleClick}>
                  <Text fontStyle="um_text-base/medium" color="static/Primary-onLight">
                    Download the app
                  </Text>
                </Button>
              </div>
            </div>
          </GridItem>
        </Grid>
      </Reveal>
    </Container>
  )
}

export default Rating
